/**
 * INJECTABLE transport. The protocol does not know whether an envelope travels over a
 * WebRTC DataChannel (direct P2P, both online) or over the blind relay (encrypted
 * mailbox for deferred delivery when the peer is offline). This lets us
 * test the WHOLE system with N clients in the same process, without a real network.
 *
 * **BLIND RELAY (central invariant):** the server only sees routing
 * (`toDevice`, `fromDevice`, `spaceId`) and an OPAQUE blob (`ct`). It never has
 * any key, so it NEVER decrypts. {@link BlindRelay.observedBytes}
 * exposes exactly what the server could inspect, so that a test
 * proves the plaintext appears nowhere.
 */

import { base64ToBytes, bytesToBase64, concatBytes, utf8Encode } from "./crypto/encoding";
import type { WireEnvelope } from "./session";

export interface Transport {
  send(envelope: WireEnvelope): Promise<void>;
}

/** Safe view of an external JSON as a record (no `as` on `unknown`). */
function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null ? { ...value } : {};
}

function mailboxKey(spaceId: string | undefined, deviceId: string): string {
  return `${spaceId ?? ""} ${deviceId}`;
}

/**
 * Blind relay + in-memory offline mailbox (models the extended signaling
 * server `apps/signaling`). Stores envelopes per (space, device) and
 * delivers them on `fetch`. Store-and-forward: if the recipient is offline,
 * the envelope waits encrypted until it connects.
 */
export class BlindRelay implements Transport {
  private readonly queues = new Map<string, WireEnvelope[]>();
  /** Copy of EVERYTHING that went through the relay (for the "never decrypts" test). */
  private readonly archive: WireEnvelope[] = [];

  async send(envelope: WireEnvelope): Promise<void> {
    this.deposit(envelope);
  }

  deposit(envelope: WireEnvelope): void {
    const key = mailboxKey(envelope.spaceId, envelope.toDevice);
    const queue = this.queues.get(key) ?? [];
    queue.push(envelope);
    this.queues.set(key, queue);
    this.archive.push(envelope);
  }

  /** Delivers and empties the device's mailbox in a space (multi-org isolation). */
  fetch(deviceId: string, spaceId?: string): WireEnvelope[] {
    const key = mailboxKey(spaceId, deviceId);
    const queue = this.queues.get(key) ?? [];
    this.queues.set(key, []);
    return queue;
  }

  pending(deviceId: string, spaceId?: string): number {
    return (this.queues.get(mailboxKey(spaceId, deviceId)) ?? []).length;
  }

  /**
   * EVERYTHING the server "sees": cleartext routing + opaque ciphertext. A test
   * checks that no key/plaintext appears here. The relay has NO way to
   * call decrypt: there is no key in its state.
   */
  observedBytes(): Uint8Array {
    const parts: Uint8Array[] = [];
    for (const env of this.archive) {
      parts.push(
        utf8Encode(env.toDevice),
        utf8Encode(env.fromDevice),
        utf8Encode(env.spaceId ?? ""),
        base64ToBytes(env.ct),
      ); // el blob opaco, crudo
    }
    return concatBytes(...parts);
  }
}

/** Injectable `fetch` (test with a fake / production with the global). */
export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * Client of the **real blind relay** (`apps/signaling`, `/api/mailbox/*` endpoints).
 * Implements `Transport` over HTTP: `send` deposits an opaque envelope; `poll`
 * collects the own mailbox by cursor and confirms with `ack`. The `fetch` is
 * injectable to test the wire format without a network.
 */
export class MailboxClient implements Transport {
  private cursor = 0;
  private token?: string;

  constructor(
    private readonly baseUrl: string,
    private readonly deviceId: string,
    private readonly space: string = "",
    private readonly fetchImpl: FetchLike = globalThis.fetch.bind(globalThis),
  ) {}

  private async json(path: string, init: RequestInit): Promise<unknown> {
    const res = await this.fetchImpl(`${this.baseUrl}${path}`, {
      ...init,
      headers: { "content-type": "application/json", ...init.headers },
    });
    if (!res.ok) {
      throw new Error(`mailbox: ${res.status}`);
    }
    return res.json();
  }

  /** Claims the device's mailbox and stores the pickup token. */
  async register(): Promise<void> {
    const body = asRecord(
      await this.json("/api/mailbox/register", {
        method: "POST",
        body: JSON.stringify({ device: this.deviceId, space: this.space }),
      }),
    );
    this.token = typeof body.deviceToken === "string" ? body.deviceToken : undefined;
  }

  async send(envelope: WireEnvelope): Promise<void> {
    await this.json("/api/mailbox/deposit", {
      method: "POST",
      body: JSON.stringify({
        to: envelope.toDevice,
        from: envelope.fromDevice,
        space: envelope.spaceId ?? "",
        blob: encodeEnvelope(envelope),
      }),
    });
  }

  /** Collects new envelopes (advances the cursor) and optionally acks them. */
  async poll(ackProcessed = true): Promise<WireEnvelope[]> {
    if (!this.token) {
      throw new Error("mailbox: register before collecting");
    }
    const q = `?space=${encodeURIComponent(this.space)}&after=${this.cursor}`;
    const body = asRecord(
      await this.json(`/api/mailbox/${encodeURIComponent(this.deviceId)}/messages${q}`, {
        method: "GET",
        headers: { "x-device-token": this.token },
      }),
    );
    const raw = Array.isArray(body.envelopes) ? body.envelopes : [];
    const envelopes: { seq: number; blob: string }[] = [];
    for (const item of raw) {
      const e = asRecord(item);
      if (typeof e.seq === "number" && typeof e.blob === "string") {
        envelopes.push({ seq: e.seq, blob: e.blob });
      }
    }
    if (envelopes.length === 0) {
      return [];
    }
    const lastSeq = envelopes.at(-1)!.seq;
    this.cursor = lastSeq;
    if (ackProcessed) {
      await this.json(`/api/mailbox/${encodeURIComponent(this.deviceId)}/ack`, {
        method: "POST",
        headers: { "x-device-token": this.token },
        body: JSON.stringify({ space: this.space, upTo: lastSeq }),
      });
    }
    return envelopes.map((e) => decodeEnvelope(e.blob));
  }
}

/** Serializes the whole envelope into an opaque blob (what the relay sees). */
export function encodeEnvelope(envelope: WireEnvelope): string {
  return bytesToBase64(utf8Encode(JSON.stringify(envelope)));
}

export function decodeEnvelope(blob: string): WireEnvelope {
  const parsed: unknown = JSON.parse(new TextDecoder().decode(base64ToBytes(blob)));
  if (typeof parsed !== "object" || parsed === null) {
    throw new Error("mailbox: invalid envelope");
  }
  const r: Record<string, unknown> = { ...parsed };
  const rh: Record<string, unknown> = typeof r.rh === "object" && r.rh !== null ? { ...r.rh } : {};
  if (
    typeof r.toDevice !== "string" ||
    typeof r.fromDevice !== "string" ||
    typeof r.ct !== "string" ||
    (r.type !== "prekey" && r.type !== "normal") ||
    typeof rh.dhPub !== "string" ||
    typeof rh.pn !== "number" ||
    typeof rh.n !== "number"
  ) {
    throw new Error("mailbox: invalid envelope shape");
  }
  let x3dh: WireEnvelope["x3dh"];
  if (typeof r.x3dh === "object" && r.x3dh !== null) {
    const x: Record<string, unknown> = { ...r.x3dh };
    if (
      typeof x.identityDhPub === "string" &&
      typeof x.ephemeralPub === "string" &&
      typeof x.usedSignedPreKeyId === "string"
    ) {
      x3dh = {
        identityDhPub: x.identityDhPub,
        ephemeralPub: x.ephemeralPub,
        usedSignedPreKeyId: x.usedSignedPreKeyId,
        usedOneTimePreKeyId:
          typeof x.usedOneTimePreKeyId === "string" ? x.usedOneTimePreKeyId : undefined,
      };
    }
  }
  return {
    v: 1,
    toDevice: r.toDevice,
    fromDevice: r.fromDevice,
    spaceId: typeof r.spaceId === "string" ? r.spaceId : undefined,
    type: r.type,
    x3dh,
    rh: { dhPub: rh.dhPub, pn: rh.pn, n: rh.n },
    ct: r.ct,
  };
}

/**
 * Direct P2P channel (models the already-established WebRTC DataChannel). `send`
 * delivers immediately to the peer's mailbox; no server intervenes.
 */
export class DirectChannel implements Transport {
  private inbox: WireEnvelope[] = [];
  peer?: DirectChannel;

  static pair(): [DirectChannel, DirectChannel] {
    const a = new DirectChannel();
    const b = new DirectChannel();
    a.peer = b;
    b.peer = a;
    return [a, b];
  }

  async send(envelope: WireEnvelope): Promise<void> {
    if (!this.peer) {
      throw new Error("DirectChannel: sin peer conectado");
    }
    this.peer.inbox.push(envelope);
  }

  drain(): WireEnvelope[] {
    const messages = this.inbox;
    this.inbox = [];
    return messages;
  }
}
